export interface Requests {
  id: string;
  name?: string;
  nameEn?: string;
  kategorie?: string;
  kategorieEn?: string;
  faehigkeit?: string;
  faehigkeitEn?: string;
  approved?: boolean;
  requestedBy?: string;
  requestedByUser?: {
    firstName: string;
    lastName: string;
    email: string
  };
}

export class RequestsFactory {
  static empty(): Requests {
    return {
      id: crypto.randomUUID(),
      name: null,
      kategorie: null,
      faehigkeit: null
    };
  }
}

export enum RequestType {
  SKILL = 'SKILL',
  CERTIFICATE = 'CERTIFICATE',
  TRAINING = 'TRAINING',
  INDUSTRY = 'INDUSTRY',
  MAINFOCUS = 'MAINFOCUS',
}

export type RequestLanguage = 'de' | 'en';

const BILINGUAL_REQUEST_TYPES: ReadonlySet<RequestType> = new Set([
  RequestType.SKILL,
  RequestType.INDUSTRY,
  RequestType.MAINFOCUS,
]);

export function isBilingualRequestType(type: RequestType): boolean {
  return BILINGUAL_REQUEST_TYPES.has(type);
}

export function requestLanguageFor(uiLanguage: string | null | undefined): RequestLanguage {
  return uiLanguage?.toLowerCase().startsWith('en') ? 'en' : 'de';
}

/** Steuerzeichen sowie unsichtbare Bidi- und Zero-Width-Zeichen, mit denen sich ein Name optisch fälschen ließe. */
const FORBIDDEN_CHARACTERS = /[\u0000-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/;

export function hasForbiddenCharacters(value: string | null | undefined): boolean {
  return !!value && FORBIDDEN_CHARACTERS.test(value);
}

export function isFilled(value: string | null | undefined): boolean {
  return !!value?.trim();
}

function trimmedOrNull(value: string | null | undefined): string | null {
  return isFilled(value) ? value.trim() : null;
}

/**
 * Der Dialog tippt sprachneutral in name/kategorie/faehigkeit; erst hier landet der Text im Feld
 * der Antragssprache (Oberflächensprache beim Öffnen).
 */
export function toLanguageRequest(draft: Requests, language: RequestLanguage): Requests {
  const name = trimmedOrNull(draft.name);
  const kategorie = trimmedOrNull(draft.kategorie);
  const faehigkeit = trimmedOrNull(draft.faehigkeit);
  if (language === 'en') {
    return {...draft, name: null, kategorie: null, faehigkeit: null, nameEn: name, kategorieEn: kategorie, faehigkeitEn: faehigkeit};
  }
  return {...draft, name, kategorie, faehigkeit, nameEn: null, kategorieEn: null, faehigkeitEn: null};
}

/** Die Namensteile einer Sprache: bei der Fähigkeit Kategorie und Fähigkeit, sonst der Name. */
export function requestNameParts(request: Requests, type: RequestType, language: RequestLanguage): (string | null)[] {
  if (type === RequestType.SKILL) {
    return language === 'en'
      ? [request?.kategorieEn ?? null, request?.faehigkeitEn ?? null]
      : [request?.kategorie ?? null, request?.faehigkeit ?? null];
  }
  return [(language === 'en' ? request?.nameEn : request?.name) ?? null];
}

export interface Page {
  size: number;
  number: number;
  totalElements: number;
  totalPages: number;
}

export interface PagedResponse<T> {
  content: T[];
  page: Page;
}
